import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useLanguage } from "@/lib/LanguageContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import { ChevronLeft, Network, ShieldCheck, Users, Stethoscope, FlaskConical, Printer, FileDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { exportNodeToPdf } from "@/lib/pdf";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = { ShieldCheck, Users, Stethoscope, FlaskConical, Network };

/* ------------------------------------------------------------------
   Struktur Organisasi RS Bhayangkara Tarakan — Model Skema Berjenjang
   Data ini simulasi hirarki organisasi rumah sakit di bawah Polri.
   ------------------------------------------------------------------ */
const DEFAULT_ORG_DATA = {
  id: "karumkit",
  title: "Kepala Rumah Sakit",
  short: "Karumkit",
  name: "dr. [Nama Pejabat], Sp.PD, M.Kes",
  rank: "AKBP",
  tint: "from-blue-700 to-blue-900",
  children: [
    {
      id: "wakarumkit",
      title: "Wakil Kepala Rumah Sakit",
      short: "Wakarumkit",
      name: "dr. [Nama Pejabat], Sp.B",
      rank: "KOMPOL",
      tint: "from-blue-600 to-blue-800",
      children: [
        {
          id: "yanmedwat",
          title: "Pelayanan Medis & Perawatan",
          short: "Kaur Yanmedwat",
          name: "dr. [Nama], Sp.PK",
          rank: "AKP",
          tint: "from-emerald-500 to-emerald-700",
          icon: "Stethoscope",
          children: [
            { id: "u-igd", title: "Instalasi Gawat Darurat", short: "IGD 24 Jam", tint: "bg-emerald-50 border-emerald-200 text-emerald-800" },
            { id: "u-ri", title: "Instalasi Rawat Inap", short: "Rawat Inap", tint: "bg-emerald-50 border-emerald-200 text-emerald-800" },
            { id: "u-rj", title: "Instalasi Rawat Jalan / Poliklinik", short: "Rawat Jalan", tint: "bg-emerald-50 border-emerald-200 text-emerald-800" },
            { id: "u-icu", title: "Instalasi ICU", short: "ICU", tint: "bg-emerald-50 border-emerald-200 text-emerald-800" },
            { id: "u-ok", title: "Instalasi Bedah Sentral (OK)", short: "Kamar Operasi", tint: "bg-emerald-50 border-emerald-200 text-emerald-800" },
            { id: "u-kebidanan", title: "Instalasi Kebidanan & Kandungan", short: "VK / OBGYN", tint: "bg-emerald-50 border-emerald-200 text-emerald-800" },
          ],
        },
        {
          id: "jangmedum",
          title: "Penunjang Medis & Umum",
          short: "Kaur Jangmedum",
          name: "[Nama], S.Kep",
          rank: "AKP",
          tint: "from-amber-500 to-amber-700",
          icon: "FlaskConical",
          children: [
            { id: "j-farmasi", title: "Instalasi Farmasi", short: "Farmasi", tint: "bg-amber-50 border-amber-200 text-amber-800" },
            { id: "j-rad", title: "Instalasi Radiologi", short: "Radiologi", tint: "bg-amber-50 border-amber-200 text-amber-800" },
            { id: "j-lab", title: "Instalasi Laboratorium", short: "Lab Klinik", tint: "bg-amber-50 border-amber-200 text-amber-800" },
            { id: "j-gizi", title: "Instalasi Gizi", short: "Gizi", tint: "bg-amber-50 border-amber-200 text-amber-800" },
            { id: "j-rm", title: "Rekam Medis & SIRS", short: "Rekam Medis", tint: "bg-amber-50 border-amber-200 text-amber-800" },
            { id: "j-cssd", title: "CSSD & Loundry", short: "CSSD", tint: "bg-amber-50 border-amber-200 text-amber-800" },
          ],
        },
      ],
    },
    {
      id: "wasintern",
      title: "Pengawasan Internal",
      short: "Kabag Wasintern",
      name: "[Nama], S.H",
      rank: "AKP",
      tint: "from-red-500 to-red-700",
      icon: "ShieldCheck",
      children: [
        { id: "w-etik", title: "Komite Etik & Hukum", short: "Etik & Hukum", tint: "bg-red-50 border-red-200 text-red-800" },
        { id: "w-audit", title: "Audit Mutu & PMKP", short: "PMKP", tint: "bg-red-50 border-red-200 text-red-800" },
      ],
    },
    {
      id: "renmin",
      title: "Perencanaan & Administrasi",
      short: "Kabag Renmin",
      name: "[Nama], S.E",
      rank: "KOMPOL",
      tint: "from-slate-600 to-slate-800",
      icon: "Users",
      children: [
        { id: "r-sdm", title: "Urusan SDM & Personel", short: "SDM", tint: "bg-slate-50 border-slate-200 text-slate-800" },
        { id: "r-keu", title: "Urusan Keuangan", short: "Keuangan", tint: "bg-slate-50 border-slate-200 text-slate-800" },
        { id: "r-log", title: "Urusan Logistik & BMN", short: "Logistik", tint: "bg-slate-50 border-slate-200 text-slate-800" },
        { id: "r-tu", title: "Tata Usaha & Umum", short: "Tata Usaha", tint: "bg-slate-50 border-slate-200 text-slate-800" },
      ],
    },
    {
      id: "binfung",
      title: "Pembinaan Fungsi",
      short: "Kabag Binfung",
      name: "dr. [Nama], M.Kes",
      rank: "KOMPOL",
      tint: "from-purple-600 to-purple-800",
      children: [
        { id: "b-diklat", title: "Diklat & Litbang", short: "Diklat", tint: "bg-purple-50 border-purple-200 text-purple-800" },
        { id: "b-humas", title: "Humas & Kerja Sama", short: "Humas", tint: "bg-purple-50 border-purple-200 text-purple-800" },
      ],
    },
  ],
};

const NodeCard = ({ node, size = "md" }) => {
  const Icon = typeof node.icon === "string" ? ICON_MAP[node.icon] : node.icon;
  const isTop = size === "xl";
  const isMid = size === "lg";
  return (
    <div
      data-testid={`org-node-${node.id}`}
      className={`relative rounded-2xl border shadow-md text-white bg-gradient-to-br ${node.tint} ${
        isTop ? "px-6 py-5 min-w-[280px]" : isMid ? "px-5 py-4 min-w-[220px]" : "px-4 py-3 min-w-[180px]"
      }`}
    >
      {Icon && (
        <div className="absolute -top-3 -right-3 h-8 w-8 rounded-full bg-white/95 text-slate-900 flex items-center justify-center shadow">
          <Icon className="h-4 w-4" />
        </div>
      )}
      <div className={`font-heading font-bold leading-tight ${isTop ? "text-lg" : "text-sm"}`}>
        {node.title}
      </div>
      <div className={`opacity-90 mt-0.5 ${isTop ? "text-sm" : "text-xs"}`}>
        {node.short}
      </div>
      {node.name && (
        <div className={`mt-2 pt-2 border-t border-white/20 ${isTop ? "text-xs" : "text-[10px]"} opacity-95`}>
          <div className="font-medium">{node.name}</div>
          {node.rank && <div className="opacity-80">Pangkat: {node.rank}</div>}
        </div>
      )}
    </div>
  );
};

const LeafBadge = ({ leaf }) => (
  <div
    data-testid={`org-leaf-${leaf.id}`}
    className={`px-3 py-2 rounded-xl border text-xs font-medium ${leaf.tint} shadow-sm`}
  >
    <div className="font-semibold">{leaf.short}</div>
    <div className="opacity-80 text-[10px] mt-0.5">{leaf.title}</div>
  </div>
);

// Vertical connector (parent → child)
const VConnector = () => <div className="w-0.5 h-6 bg-slate-300 mx-auto" />;
// Horizontal group connector (child siblings)
const HConnector = () => <div className="w-full h-0.5 bg-slate-300" />;

const StrukturOrganisasi = () => {
  const { t } = useLanguage();
  const [orgData, setOrgData] = useState(DEFAULT_ORG_DATA);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const handlePrint = () => window.print();

  const handleDownloadPdf = async () => {
    const node = document.querySelector(".org-chart");
    if (!node) {
      toast.error("Bagan belum siap");
      return;
    }
    setDownloadingPdf(true);
    try {
      await exportNodeToPdf({
        node,
        filename: "struktur-organisasi-rs-bhayangkara-tarakan.pdf",
        orientation: "landscape",
        logoUrl: "/logo.jpg",
        headingLines: [
          "RS BHAYANGKARA TARAKAN",
          "Polda Kalimantan Utara",
          "STRUKTUR ORGANISASI",
        ],
      });
      toast.success("PDF berhasil diunduh");
    } catch (e) {
      toast.error("Gagal membuat PDF");
    } finally {
      setDownloadingPdf(false);
    }
  };

  useEffect(() => {
    axios
      .get(`${API}/site-content/struktur_organisasi`)
      .then((r) => {
        if (r.data && r.data.doc) setOrgData(r.data.doc);
      })
      .catch(() => {
        /* keep defaults */
      });
  }, []);

  const ORG_DATA = orgData;

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 print-struktur" data-testid="struktur-organisasi-page">
      <div className="no-print"><Header /></div>
      <main className="flex-1 pt-24 pb-16 print:pt-4">
      <div className="container-x">
        {/* Header — sembunyi saat print */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-8 no-print">
          <div>
            <Link
              to="/"
              data-testid="struktur-back-link"
              className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-blue-800 mb-2"
            >
              <ChevronLeft className="h-4 w-4" />
              {t.nav.home}
            </Link>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 flex items-center gap-2">
              <Network className="h-7 w-7 text-blue-800" />
              Struktur Organisasi
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              RS Bhayangkara Tarakan — Polda Kalimantan Utara
            </p>
          </div>
          <div className="flex items-center gap-2">
          <button
            onClick={handlePrint}
            data-testid="struktur-print-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium bg-blue-800 hover:bg-blue-900 text-white shadow-md"
          >
            <Printer className="h-4 w-4" />
            Cetak Bagan
          </button>
          <button
            onClick={handleDownloadPdf}
            disabled={downloadingPdf}
            data-testid="struktur-download-pdf-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium bg-emerald-600 hover:bg-emerald-700 text-white shadow-md disabled:opacity-60"
          >
            {downloadingPdf ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileDown className="h-4 w-4" />
            )}
            {downloadingPdf ? "Menyiapkan..." : "Unduh PDF"}
          </button>
          </div>
        </div>

        {/* Print-only header */}
        <div className="hidden print:block mb-6 text-center border-b pb-4">
          <div className="text-lg font-bold">RS BHAYANGKARA TARAKAN</div>
          <div className="text-sm">Polda Kalimantan Utara</div>
          <div className="text-base font-semibold mt-2">STRUKTUR ORGANISASI</div>
        </div>

        {/* Tree — horizontally scrollable on small screens */}
        <div className="overflow-x-auto pb-8 org-print-scroll">
          <div className="min-w-[1100px] flex flex-col items-center org-chart">
            {/* Level 1 — Karumkit */}
            <NodeCard node={ORG_DATA} size="xl" />
            <VConnector />

            {/* Level 2 — 4 bagian di bawah Karumkit (Wakarumkit sentral + 3 kabag) */}
            <div className="grid grid-cols-4 gap-8 relative w-full">
              {/* Horizontal spine connecting siblings */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[85%] h-0.5 bg-slate-300" style={{ top: "-1px" }} />
              {ORG_DATA.children.map((lv2) => (
                <div key={lv2.id} className="flex flex-col items-center">
                  <VConnector />
                  <NodeCard node={lv2} size="lg" />

                  {/* Level 3 children */}
                  {lv2.children && lv2.children.length > 0 && (
                    <>
                      <VConnector />
                      {/* If sub-heads (Yanmedwat / Jangmedum under Wakarumkit) have their own children — render as 2 tiers */}
                      {lv2.children[0].children ? (
                        <div className="flex flex-col gap-6 w-full">
                          {lv2.children.map((lv3) => (
                            <div key={lv3.id} className="flex flex-col items-center">
                              <NodeCard node={lv3} size="md" />
                              <VConnector />
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 w-full max-w-md">
                                {lv3.children.map((leaf) => (
                                  <LeafBadge key={leaf.id} leaf={leaf} />
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="flex flex-col gap-2 w-full max-w-xs">
                          {lv2.children.map((leaf) => (
                            <LeafBadge key={leaf.id} leaf={leaf} />
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="mt-8 p-5 rounded-2xl bg-white border border-slate-100 shadow-sm no-print">
          <h3 className="font-heading font-semibold text-slate-900 mb-3">Keterangan Skema</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 rounded bg-gradient-to-br from-blue-700 to-blue-900" />
              <span>Pimpinan (Karumkit / Wakarumkit)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 rounded bg-gradient-to-br from-emerald-500 to-emerald-700" />
              <span>Pelayanan Medis</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 rounded bg-gradient-to-br from-amber-500 to-amber-700" />
              <span>Penunjang Medis</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 rounded bg-gradient-to-br from-red-500 to-red-700" />
              <span>Pengawasan Internal</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 rounded bg-gradient-to-br from-slate-600 to-slate-800" />
              <span>Administrasi</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 rounded bg-gradient-to-br from-purple-600 to-purple-800" />
              <span>Pembinaan Fungsi</span>
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-4">
            <b>Catatan:</b> Nama pejabat dalam bagan ini bersifat simulasi. Data resmi terbaru
            dapat diperoleh melalui bagian Humas RS Bhayangkara Tarakan.
          </p>
        </div>
      </div>
      </main>
      <div className="no-print"><Footer /></div>
      <div className="no-print"><FloatingWhatsApp /></div>
    </div>
  );
};

export default StrukturOrganisasi;
